import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause, 
  MessageCircle, 
  Heart, 
  Share2, 
  MoreHorizontal, 
  X, 
  Volume2, 
  Plus,
  Brain,
  Mic,
  BarChart3,
  Image as ImageIcon,
  Lock,
  Users,
  UserCheck,
  Shield,
  Sparkles,
  Camera,
  Video,
  Globe,
  Eye,
  Send,
  ArrowUp,
  Zap,
  Star,
  Smile,
  ThumbsUp,
  Share,
  Bookmark,
  ChevronDown,
  Filter,
  Search,
  User,
  VolumeX
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import { SCREENS } from '../../utils/constants';
import { useTranslation } from 'react-i18next';
import { t as globalT } from '../../i18n';

const IdentityFeed = ({ 
  currentTheme,
  t: propT
}) => {
  const { user } = useAuth();
  const { navigateToScreen, addFeedItem, feedItems, updateFeedItem } = useUI();
  const { t } = useTranslation();
  
  // Use react-i18next translation if available, fallback to global i18n, then prop
  const translate = useCallback((key, fallback) => {
    // Try react-i18next first
    if (t && typeof t === 'function') {
      const result = t(key);
      if (result && result !== key) {
        return result;
      }
    }
    
    // Try global i18n
    if (globalT && typeof globalT === 'function') {
      const result = globalT(key);
      if (result && result !== key) {
        return result;
      }
    }
    
    // Try prop translation
    if (propT && typeof propT === 'function') {
      const result = propT(key);
      if (result && result !== key) {
        return result;
      }
    }
    
    // Use fallback or return a user-friendly default
    if (fallback) {
      return fallback;
    }
    
    // Return a sensible default based on the key
    const keyDefaults = {
      'identityCircle.feed.mediaShare': 'Medya Paylaş',
      'identityCircle.feed.mediaDescription': 'Fotoğraf, video ve anlar',
      'identityCircle.feed.thoughtShare': 'Düşünce Paylaş',
      'identityCircle.feed.thoughtDescription': 'Fikirler ve düşünceler',
      'identityCircle.feed.voiceNote': 'Sesli Not',
      'identityCircle.feed.voiceDescription': 'Sesli mesajlar ve hikayeler',
      'identityCircle.feed.statsShare': 'İstatistik Paylaş',
      'identityCircle.feed.statsDescription': 'Başarılar ve metrikler',
      'identityCircle.feed.digitalVault': 'Dijital Kasa',
      'identityCircle.feed.vaultDescription': 'Özel ve şifrelenmiş depolama',
      'identityCircle.feed.circle': 'Circle',
      'identityCircle.feed.circleDescription': 'Tüm circle üyelerine görünür',
      'identityCircle.feed.closeCircle': 'Yakın Circle',
      'identityCircle.feed.closeDescription': 'Sadece yakın arkadaşların görebilir',
      'identityCircle.feed.selectedPeople': 'Seçili Kişiler',
      'identityCircle.feed.selectedDescription': 'Belirlediğin kişiler görebilir',
      'identityCircle.feed.vaultPrivacy': 'Dijital Kasa',
      'identityCircle.feed.vaultPrivacyDescription': 'Sadece sen, şifrelenmiş',
      'identityCircle.feed.privacyTitle': 'Gizlilik Ayarları',
      'identityCircle.feed.privacyDescription': 'İçeriğini kimler görebilsin?',
      'identityCircle.feed.createTitle': 'İçerik Oluştur',
      'identityCircle.feed.createDescription': 'Düşüncelerini dünya ile paylaş',
      'identityCircle.feed.createPlaceholder': 'Ne düşünüyorsun?',
      'identityCircle.feed.close': 'Kapat',
      'identityCircle.feed.level': 'Seviye',
      'identityCircle.feed.xpProgress': 'XP İlerlemesi',
      'identityCircle.feed.compatibility': 'uyumluluk',
      'identityCircle.feed.message': 'Mesaj',
      'identityCircle.feed.profile': 'Profil',
      'identityCircle.feed.mute': 'Sessize Al',
      'identityCircle.feed.recentActivity': 'Son Aktiviteler',
      'identityCircle.feed.mutualConnections': 'Ortak Bağlantılar',
      'identityCircle.feed.more': 'daha',
      'identityCircle.feed.swipeToClose': 'Kapatmak için aşağı kaydır',
      'identityCircle.feed.commentPlaceholder': 'Yorum yaz...',
      'identityCircle.feed.voiceNoteLabel': 'Sesli Not',
      'identityCircle.feed.you': 'Sen',
      'identityCircle.feed.lastSeen': 'Son görülme bilinmiyor'
    };
    
    return keyDefaults[key] || key.split('.').pop() || key;
  }, [t, globalT, propT]);
  
  const [showComments, setShowComments] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [selectedCreateType, setSelectedCreateType] = useState(null);
  const [selectedPrivacy, setSelectedPrivacy] = useState('circle');
  const [comment, setComment] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [showShareModal, setShowShareModal] = useState(null);
  const [showRewardAnimation, setShowRewardAnimation] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const audioRef = useRef(null);
  const scrollTimeout = useRef(null);
  const feedContainerRef = useRef(null);

  // Enhanced Create content options - dopamine optimized
  const createOptions = useCallback(() => [
    {
      id: 'media',
      title: translate('identityCircle.feed.mediaShare'),
      emoji: '🎭',
      icon: Camera,
      gradient: 'from-violet-500 via-purple-600 to-fuchsia-600',
      glowColor: 'rgb(139, 92, 246)',
      screen: SCREENS.MEDIA_CREATION,
      description: translate('identityCircle.feed.mediaDescription')
    },
    {
      id: 'thought',
      title: translate('identityCircle.feed.thoughtShare'),
      emoji: '🧠',
      icon: Brain,
      gradient: 'from-cyan-400 via-blue-500 to-indigo-600',
      glowColor: 'rgb(59, 130, 246)',
      screen: SCREENS.TEXT_CREATION,
      description: translate('identityCircle.feed.thoughtDescription')
    },
    {
      id: 'voice',
      title: translate('identityCircle.feed.voiceNote'),
      emoji: '🎤',
      icon: Mic,
      gradient: 'from-emerald-400 via-teal-500 to-cyan-600',
      glowColor: 'rgb(20, 184, 166)',
      screen: SCREENS.VOICE_CREATION,
      description: translate('identityCircle.feed.voiceDescription')
    },
    {
      id: 'stats',
      title: translate('identityCircle.feed.statsShare'),
      emoji: '📊',
      icon: BarChart3,
      gradient: 'from-orange-400 via-pink-500 to-rose-600',
      glowColor: 'rgb(236, 72, 153)',
      screen: SCREENS.UNIFIED_STATISTICS,
      description: translate('identityCircle.feed.statsDescription')
    },
    {
      id: 'vault',
      title: translate('identityCircle.feed.digitalVault'),
      emoji: '🔒',
      icon: Lock,
      gradient: 'from-slate-600 via-zinc-700 to-gray-800',
      glowColor: 'rgb(71, 85, 105)',
      screen: SCREENS.DIGITAL_VAULT,
      description: translate('identityCircle.feed.vaultDescription')
    }
  ], [translate]);

  // Enhanced Privacy options with dopamine-optimized design
  const privacyOptions = useCallback(() => [
    {
      id: 'circle',
      title: translate('identityCircle.feed.circle'),
      description: translate('identityCircle.feed.circleDescription'),
      icon: Users,
      gradient: 'from-emerald-400 to-green-500',
      glowColor: 'rgb(34, 197, 94)',
      emoji: '🌐'
    },
    {
      id: 'close',
      title: translate('identityCircle.feed.closeCircle'),
      description: translate('identityCircle.feed.closeDescription'),
      icon: UserCheck,
      gradient: 'from-amber-400 to-orange-500',
      glowColor: 'rgb(245, 158, 11)',
      emoji: '💫'
    },
    {
      id: 'selected',
      title: translate('identityCircle.feed.selectedPeople'),
      description: translate('identityCircle.feed.selectedDescription'),
      icon: Eye,
      gradient: 'from-blue-400 to-cyan-500',
      glowColor: 'rgb(59, 130, 246)',
      emoji: '👁️'
    },
    {
      id: 'vault',
      title: translate('identityCircle.feed.vaultPrivacy'),
      description: translate('identityCircle.feed.vaultPrivacyDescription'),
      icon: Lock,
      gradient: 'from-purple-500 to-violet-600',
      glowColor: 'rgb(139, 92, 246)',
      emoji: '🔒'
    }
  ], [translate]);

  // Enhanced haptic feedback with patterns
  const triggerHaptic = useCallback((type = 'soft') => {
    if (window.navigator && window.navigator.vibrate) {
      const patterns = {
        soft: [10],
        medium: [20],
        strong: [30],
        double: [10, 50, 20],
        reward: [15, 50, 15, 50, 25], // Dopamine reward pattern
        error: [100, 50, 100],
        success: [25, 25, 50]
      };
      window.navigator.vibrate(patterns[type] || patterns.soft);
    }
  }, []);

  // Scroll handling for enhanced UX
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setShowScrollTop(currentScrollY > 500);
      setIsScrolling(true);

      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Set new timeout to detect scroll end
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  // Smooth scroll to top
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    triggerHaptic('soft');
  }, [triggerHaptic]);

  // Enhanced create content with reward system
  const handleCreateContent = useCallback((type) => {
    triggerHaptic('medium');
    setSelectedCreateType(type);
    
    const option = createOptions().find(opt => opt.id === type);
    
    if (option?.screen) {
      setSelectedCreateType(type);
      
      // Show privacy selection for all content types (dopamine-optimized flow)
      setShowCreateModal(false);
      setShowPrivacyModal(true);
      
      // Trigger reward animation for engagement
      setShowRewardAnimation(type);
      setTimeout(() => setShowRewardAnimation(null), 1500);
    }
  }, [navigateToScreen, triggerHaptic, createOptions, translate]);

  // Enhanced privacy selection with smooth transitions
  const handlePrivacySelect = useCallback((privacy) => {
    setSelectedPrivacy(privacy);
    setShowPrivacyModal(false);
    triggerHaptic('success');
    
    // Navigate based on selected content type
    const option = createOptions().find(opt => opt.id === selectedCreateType);
    if (option?.screen) {
      navigateToScreen(option.screen, { 
        contentType: selectedCreateType,
        privacy: privacy,
        isShareMode: true
      });
    }
  }, [selectedCreateType, navigateToScreen, createOptions, triggerHaptic, translate]);

  // Enhanced demo feed data with diverse content types
  const demoFeedData = [
    {
      id: 1,
      type: 'thought',
      user: {
        name: 'Alex Chen',
        profilePhoto: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        avatar: 'AC',
        bio: 'Digital artist & creative technologist',
        location: 'San Francisco, CA',
        joinDate: 'March 2024',
        stats: { posts: 142, connections: 1.2, followers: 3.4 }
      },
      content: '🌅 Just finished an incredible sunrise hike! The view from the peak was absolutely breathtaking. There\'s something magical about watching the world wake up from above the clouds. #MindfulMoments #NatureTherapy',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      likes: 24,
      privacy: 'circle',
      comments: [
        { id: 1, user: 'Jordan Kim', text: 'Amazing! Which trail did you take? 🥾', timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000), reactions: [{ emoji: '❤️', count: 2 }] },
        { id: 2, user: 'Sam Rivera', text: 'Stunning view! 🏔️✨', timestamp: new Date(Date.now() - 30 * 60 * 1000), reactions: [{ emoji: '🔥', count: 3 }] }
      ],
      shares: 5,
      tags: ['mindfulness', 'nature', 'wellness']
    },
    {
      id: 2,
      type: 'voice',
      user: {
        name: 'Maya Patel',
        profilePhoto: 'https://images.unsplash.com/photo-1494790108755-2616b612b631?w=150&h=150&fit=crop&crop=face',
        avatar: 'MP',
        bio: 'Musician & audio engineer',
        location: 'London, UK',
        joinDate: 'January 2024',
        stats: { posts: 89, connections: 2.1, followers: 5.8 }
      },
      content: '🎵 Quick thoughts on the new album I\'ve been working on... The creative process has been absolutely mind-blowing!',
      audioUrl: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav',
      duration: '0:42',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      likes: 18,
      privacy: 'close',
      comments: [
        { id: 1, user: 'Chris Wong', text: 'Can\'t wait to hear the full album! 🎧', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), reactions: [{ emoji: '🎵', count: 4 }] }
      ],
      shares: 3,
      tags: ['music', 'creativity', 'production']
    },
    {
      id: 3,
      type: 'media',
      user: {
        name: 'David Rodriguez',
        profilePhoto: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        avatar: 'DR',
        bio: 'Travel photographer',
        location: 'Barcelona, Spain',
        joinDate: 'February 2024',
        stats: { posts: 203, connections: 856, followers: 2.3 }
      },
      content: '✨ Golden hour magic in the old town. Sometimes the universe just aligns perfectly for that one shot.',
      imageUrl: 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=600&h=400&fit=crop',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      likes: 56,
      privacy: 'circle',
      comments: [
        { id: 1, user: 'Emma Stone', text: 'Incredible lighting! 📸', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), reactions: [{ emoji: '✨', count: 6 }] },
        { id: 2, user: 'Jake Wilson', text: 'Perfect composition �', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), reactions: [{ emoji: '🔥', count: 8 }] }
      ],
      shares: 12,
      tags: ['photography', 'goldenhour', 'travel']
    },
    {
      id: 4,
      type: 'stats',
      user: {
        name: 'Lisa Thompson',
        profilePhoto: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
        avatar: 'LT',
        bio: 'Wellness coach & fitness enthusiast',
        location: 'Austin, TX',
        joinDate: 'April 2024',
        stats: { posts: 167, connections: 943, followers: 1.8 }
      },
      content: '💪 Just crushed my personal best! Consistency is everything.',
      statistics: {
        title: 'Morning Run Achievement',
        metrics: [
          { label: 'Distance', value: '8.2', unit: 'km', trend: '+12%' },
          { label: 'Time', value: '42:15', unit: '', trend: '-5%' },
          { label: 'Pace', value: '5:09', unit: '/km', trend: 'PB' },
          { label: 'Calories', value: '487', unit: '', trend: '+8%' }
        ]
      },
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000),
      likes: 31,
      privacy: 'circle',
      comments: [
        { id: 1, user: 'Mike Johnson', text: 'Beast mode activated! 💪', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), reactions: [{ emoji: '💪', count: 12 }] }
      ],
      shares: 8,
      tags: ['fitness', 'running', 'achievement']
    },
    {
      id: 5,
      type: 'media',
      user: {
        name: 'Sarah Kim',
        profilePhoto: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=150&h=150&fit=crop&crop=face',
        avatar: 'SK',
        bio: 'Content creator & storyteller',
        location: 'New York, NY',
        joinDate: 'May 2024',
        stats: { posts: 94, connections: 1.5, followers: 4.2 }
      },
      content: '🎬 Behind the scenes of today\'s creative session. The magic happens in the process!',
      videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
      likes: 42,
      privacy: 'selected',
      comments: [
        { id: 1, user: 'Tom Brown', text: 'Love seeing the creative process! 🎨', timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000), reactions: [{ emoji: '🎨', count: 5 }] },
        { id: 2, user: 'Anna Lee', text: 'So inspiring! ✨', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000), reactions: [{ emoji: '✨', count: 7 }] }
      ],
      shares: 15,
      tags: ['creativity', 'behindthescenes', 'content']
    }
  ];

  // Combine demo data with real feed items from context
  const allFeedData = [...feedItems, ...demoFeedData]
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const handlePlayAudio = async (postId, audioUrl) => {
    try {
      if (playingAudio === postId) {
        // Stop current audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        setPlayingAudio(null);
      } else {
        // Stop any currently playing audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        
        // Start new audio
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlayingAudio(null);
        };
        
        audio.onerror = (e) => {
          console.warn('Audio playback failed:', e);
          setPlayingAudio(null);
        };
        
        await audio.play();
        setPlayingAudio(postId);
      }
    } catch (error) {
      console.warn('Audio playback error:', error);
      setPlayingAudio(null);
    }
  };

  // Enhanced like handler with debouncing and dopamine feedback
  const handleLike = useCallback((postId) => {
    // Find if this is a feed item or demo data
    const feedItemExists = feedItems.find(item => item.id === postId);
    const isCurrentlyLiked = likedPosts.has(postId);
    
    if (feedItemExists) {
      // Update feed item in context
      const currentLikes = feedItemExists.likes || 0;
      
      updateFeedItem(postId, {
        likes: isCurrentlyLiked ? currentLikes - 1 : currentLikes + 1
      });
    }
    
    // Update local liked state
    setLikedPosts(prev => {
      const newLiked = new Set(prev);
      if (newLiked.has(postId)) {
        newLiked.delete(postId);
        triggerHaptic('soft');
      } else {
        newLiked.add(postId);
        triggerHaptic('reward'); // Dopamine reward pattern
        
        // Show reward animation
        setShowRewardAnimation(`like-${postId}`);
        setTimeout(() => setShowRewardAnimation(null), 1200);
      }
      return newLiked;
    });
  }, [feedItems, updateFeedItem, likedPosts, triggerHaptic]);

  // Enhanced comment handler with smooth animations
  const handleAddComment = useCallback((postId) => {
    if (comment.trim()) {
      const newComment = {
        id: Date.now(),
        user: user?.name || translate('identityCircle.feed.you'),
        text: comment.trim(),
        timestamp: new Date(),
        reactions: []
      };

      // Find if this is a feed item or demo data
      const feedItemExists = feedItems.find(item => item.id === postId);
      
      if (feedItemExists) {
        // Update feed item in context
        const currentComments = feedItemExists.comments || [];
        updateFeedItem(postId, {
          comments: [...currentComments, newComment]
        });
      }
      
      setComment('');
      triggerHaptic('success');
      
      // Show reward animation for engagement
      setShowRewardAnimation(`comment-${postId}`);
      setTimeout(() => setShowRewardAnimation(null), 1000);
    }
  }, [comment, user, feedItems, updateFeedItem, triggerHaptic, translate]);

  // Enhanced share handler with modal
  const handleShare = useCallback((postId) => {
    setShowShareModal(postId);
    triggerHaptic('medium');
  }, [triggerHaptic]);

  // Share confirmation
  const confirmShare = useCallback((postId, shareType = 'circle') => {
    // In real app, this would make API call
    triggerHaptic('success');
    setShowShareModal(null);
    
    // Show success animation
    setShowRewardAnimation(`share-${postId}`);
    setTimeout(() => setShowRewardAnimation(null), 1500);
  }, [triggerHaptic]);

  const handleEmojiReaction = (commentId, emoji) => {
    // In a real app, this would make an API call
    console.log('Adding reaction:', { commentId, emoji });
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    // Format as "Mon DD" for older dates
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[timestamp.getMonth()]} ${timestamp.getDate()}`;
  };

  const getProfilePhotoUrl = (profilePhoto) => {
    if (!profilePhoto) {
      return 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face';
    }
    return profilePhoto;
  };

  const handleProfileClick = (postUser) => {
    setShowProfileModal(postUser);
    triggerHaptic('medium');
  };

  const closeProfileModal = () => {
    setShowProfileModal(null);
  };

  const isOwnProfile = (postUser) => {
    return user && postUser && (user.name === postUser.name || user.email === postUser.email);
  };

  const ProfileModal = ({ user: profileUser, onClose }) => {
    const isOwn = isOwnProfile(profileUser);
    const [isFollowing, setIsFollowing] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    
    // Calculate level from mock XP data
    const mockXP = profileUser.xp || 1250;
    const level = Math.floor(mockXP / 200) + 1;
    const currentLevelXP = (level - 1) * 200;
    const nextLevelXP = level * 200;
    const progressXP = mockXP - currentLevelXP;
    const progressPercentage = (progressXP / 200) * 100;

    // Mock data for recent activity
    const mockPosts = [
      {
        id: 1,
        content: "Az önce harika bir antrenman yaptım! 💪",
        timestamp: "2 saat önce",
        likes: 15,
        type: "achievement"
      },
      {
        id: 2,
        content: "Bugün heyecan verici projeler üzerinde çalışıyorum 🚀",
        timestamp: "1 gün önce", 
        likes: 8,
        type: "update"
      }
    ];

    const mockMutualConnections = [
      { name: "Sarah Chen", avatar: "S" },
      { name: "Alex Kim", avatar: "A" },
      { name: "Dev Team", avatar: "D", isGroup: true }
    ];

    const handleSwipeDown = (event, info) => {
      if (info.offset.y > 100 && info.velocity.y > 0) {
        onClose();
      }
    };

    const handleFollow = () => {
      setIsFollowing(!isFollowing);
    };

    const handleNotifications = () => {
      setNotificationsEnabled(!notificationsEnabled);
    };

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ 
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ 
              type: "spring",
              damping: 25,
              stiffness: 300,
              duration: 0.4
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 200 }}
            onDragEnd={handleSwipeDown}
            className="relative w-full max-w-md mx-auto rounded-3xl overflow-hidden"
            style={{
              background: 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag Indicator */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-white/20 rounded-full"></div>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            >
              <X size={20} className="text-white" />
            </button>

            {/* Profile Header */}
            <div className="relative px-6 pt-4 pb-8">
              {/* Avatar with Glow */}
              <div className="flex justify-center mb-4">
                <div className="relative">
                  {/* Glow Effect */}
                  <div 
                    className="absolute inset-0 rounded-full blur-xl"
                    style={{
                      background: 'radial-gradient(circle, rgba(139, 92, 246, 0.6) 0%, rgba(236, 72, 153, 0.4) 50%, transparent 70%)',
                      transform: 'scale(1.5)'
                    }}
                  ></div>
                  
                  {/* Avatar Circle */}
                  <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center border-2 border-white/20">
                    {profileUser.profilePhoto ? (
                      <img
                        src={getProfilePhotoUrl(profileUser.profilePhoto)}
                        alt={profileUser.name}
                        className="w-full h-full rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span className="text-3xl font-bold text-white" style={{ display: profileUser.profilePhoto ? 'none' : 'block' }}>
                      {profileUser.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>

                  {/* Online Status */}
                  {profileUser.isOnline && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-3 border-slate-800 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Name and Level */}
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold text-white mb-1">{profileUser.name}</h2>
                <p className="text-gray-300 text-sm mb-3">{translate('identityCircle.feed.level')} {level} • {profileUser.lastActive || translate('identityCircle.feed.lastSeen')}</p>
                
                {/* Compatibility Score */}
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30">
                  <span className="text-lg mr-1">🧬</span>
                  <span className="text-purple-300 font-medium">{profileUser.compatibility || 87}% {translate('identityCircle.feed.compatibility')}</span>
                </div>
              </div>

              {/* XP Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-400">{translate('identityCircle.feed.xpProgress')}</span>
                  <span className="text-sm text-purple-300">{mockXP} / {nextLevelXP}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                  ></motion.div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-6 pb-6">
              <div className="grid grid-cols-3 gap-3 mb-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    // Handle messaging
                    console.log('Message user:', profileUser);
                  }}
                  className="flex flex-col items-center p-4 rounded-2xl bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 hover:border-blue-300/50 transition-all"
                >
                  <MessageCircle size={24} className="text-blue-300 mb-2" />
                  <span className="text-xs text-blue-200 font-medium">{translate('identityCircle.feed.message')}</span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center p-4 rounded-2xl bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-400/30 hover:border-purple-300/50 transition-all"
                >
                  <User size={24} className="text-purple-300 mb-2" />
                  <span className="text-xs text-purple-200 font-medium">{translate('identityCircle.feed.profile')}</span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center p-4 rounded-2xl bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-400/30 hover:border-red-300/50 transition-all"
                >
                  <VolumeX size={24} className="text-red-300 mb-2" />
                  <span className="text-xs text-red-200 font-medium">{translate('identityCircle.feed.mute')}</span>
                </motion.button>
              </div>
            </div>

            {/* Recent Posts */}
            <div className="px-6 pb-4">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Zap size={18} className="mr-2 text-yellow-400" />
                {translate('identityCircle.feed.recentActivity')}
              </h3>
              <div className="space-y-3 max-h-40 overflow-y-auto">
                {mockPosts.map((post) => (
                  <div key={post.id} className="p-3 rounded-xl bg-gray-800/50 border border-gray-700/50">
                    <p className="text-sm text-gray-200 mb-1">{post.content}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{post.timestamp}</span>
                      <div className="flex items-center text-xs text-gray-400">
                        <Heart size={12} className="mr-1" />
                        {post.likes}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mutual Connections */}
            <div className="px-6 pb-6">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center">
                <Users size={16} className="mr-2 text-green-400" />
                {translate('identityCircle.feed.mutualConnections')}
              </h3>
              <div className="flex items-center space-x-3">
                {mockMutualConnections.map((connection, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                      connection.isGroup 
                        ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                        : 'bg-gradient-to-br from-blue-500 to-purple-500'
                    }`}>
                      {connection.avatar}
                    </div>
                    <span className="text-xs text-gray-300 mt-1 max-w-[3rem] truncate">
                      {connection.name}
                    </span>
                  </div>
                ))}
                <div className="flex items-center text-gray-400">
                  <span className="text-xs">+2 {translate('identityCircle.feed.more')}</span>
                </div>
              </div>
            </div>

            {/* Bottom Gesture Hint */}
            <div className="flex justify-center pb-4">
              <div className="flex items-center text-gray-500 text-xs">
                <ChevronDown size={14} className="mr-1" />
                {translate('identityCircle.feed.swipeToClose')}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  const renderPost = useCallback((post) => {
    // Güvenli user object kontrolü
    if (!post || !post.user) {
      console.warn('Post or user object is missing:', post);
      return null;
    }

    const isLiked = likedPosts.has(post.id);
    const postComments = post.comments || [];
    const showingComments = showComments === post.id;
    const privacyOption = privacyOptions().find(p => p.id === post.privacy) || privacyOptions()[0];

    return (
      <motion.article
        key={post.id}
        className={`mb-6 ${currentTheme?.cardBg || 'bg-white/80'} backdrop-blur-xl rounded-3xl border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group relative`}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -5, scale: 1.02 }}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
        }}
      >
        {/* Post header */}
        <div className="p-6 pb-4">
          <div className="flex items-start space-x-4">
            <motion.div
              className="relative"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <img
                src={getProfilePhotoUrl(post.user?.profilePhoto)}
                alt={post.user?.name || 'User'}
                className="w-12 h-12 rounded-full object-cover border-2 border-white/30 shadow-lg cursor-pointer"
                onClick={() => handleProfileClick(post.user)}
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face';
                }}
              />
              {/* Online indicator */}
              <motion.div
                className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm"
                animate={{
                  scale: [1, 1.2, 1],
                  boxShadow: [
                    "0 0 0 0 rgba(34, 197, 94, 0.7)",
                    "0 0 0 10px rgba(34, 197, 94, 0)",
                    "0 0 0 0 rgba(34, 197, 94, 0)"
                  ]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 
                  className={`font-semibold ${currentTheme?.textPrimary || 'text-gray-900'} cursor-pointer hover:text-blue-600 transition-colors`}
                  onClick={() => handleProfileClick(post.user)}
                >
                  {post.user?.name || 'Unknown User'}
                </h3>
                
                {/* Privacy indicator */}
                <motion.div
                  className={`px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${privacyOption.gradient} text-white shadow-sm`}
                  whileHover={{ scale: 1.05 }}
                  title={privacyOption.description}
                >
                  {privacyOption.emoji}
                </motion.div>
                
                {/* Post type indicator */}
                <motion.div
                  className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium text-gray-600"
                  whileHover={{ scale: 1.05 }}
                >
                  {post.type === 'thought' ? '💭' : post.type === 'voice' ? '🎤' : post.type === 'media' ? '🎨' : '📊'}
                </motion.div>
              </div>
              
              <div className="flex items-center space-x-2 mt-1">
                <span className={`text-sm ${currentTheme?.textSecondary || 'text-gray-500'}`}>
                  {formatTimestamp(post.timestamp)}
                </span>
                
                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex space-x-1">
                    {post.tags.slice(0, 2).map((tag, index) => (
                      <motion.span
                        key={tag}
                        className="text-xs text-blue-600 font-medium hover:text-blue-800 cursor-pointer"
                        whileHover={{ scale: 1.1 }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        #{tag}
                      </motion.span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <motion.button
              className={`p-2 rounded-full ${currentTheme?.hover || 'hover:bg-gray-100'} transition-colors`}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <MoreHorizontal className={`w-5 h-5 ${currentTheme?.textSecondary || 'text-gray-500'}`} />
            </motion.button>
          </div>
        </div>

        {/* Post content */}
        <div className="px-6 pb-4">
          {post.content && (
            <motion.p 
              className={`${currentTheme?.textPrimary || 'text-gray-900'} text-lg leading-relaxed mb-4`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {post.content}
            </motion.p>
          )}

          {/* Media content */}
          {post.imageUrl && (
            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg mb-4"
              whileHover={{ scale: 1.02 }}
              layoutId={`image-${post.id}`}
            >
              <img
                src={post.imageUrl}
                alt="Post content"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>
          )}

          {/* Voice note */}
          {post.audioUrl && (
            <motion.div
              className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl p-4 mb-4 border border-blue-200/30"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center space-x-4">
                <motion.button
                  onClick={() => handlePlayAudio(post.id, post.audioUrl)}
                  className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white shadow-lg"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  animate={playingAudio === post.id ? {
                    boxShadow: [
                      "0 0 0 0 rgba(59, 130, 246, 0.7)",
                      "0 0 0 20px rgba(59, 130, 246, 0)",
                      "0 0 0 0 rgba(59, 130, 246, 0)"
                    ]
                  } : {}}
                  transition={{
                    boxShadow: {
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }
                  }}
                >
                  {playingAudio === post.id ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                </motion.button>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Volume2 className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">{translate('identityCircle.feed.voiceNoteLabel')}</span>
                    <span className="text-xs text-blue-600">{post.duration}</span>
                  </div>
                  
                  {/* Audio waveform visualization */}
                  <div className="flex items-center space-x-1 mt-2">
                    {[...Array(20)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="w-1 bg-blue-400 rounded-full"
                        style={{ height: `${Math.random() * 20 + 8}px` }}
                        animate={playingAudio === post.id ? {
                          scaleY: [1, 1.5, 1],
                          opacity: [0.5, 1, 0.5]
                        } : {}}
                        transition={{
                          duration: 0.5,
                          repeat: Infinity,
                          delay: i * 0.1,
                          ease: "easeInOut"
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Statistics */}
          {post.statistics && (
            <motion.div
              className="relative overflow-hidden rounded-3xl p-6 mb-4 shadow-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(139, 92, 246, 0.1) 50%, rgba(59, 130, 246, 0.1) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(236, 72, 153, 0.2)'
              }}
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 20px 40px rgba(236, 72, 153, 0.3)"
              }}
              animate={{
                boxShadow: [
                  "0 10px 25px rgba(236, 72, 153, 0.2)",
                  "0 15px 35px rgba(139, 92, 246, 0.3)",
                  "0 10px 25px rgba(59, 130, 246, 0.2)",
                  "0 15px 35px rgba(236, 72, 153, 0.3)"
                ]
              }}
              transition={{
                boxShadow: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
            >
              {/* Animated background gradient */}
              <motion.div
                className="absolute inset-0 opacity-30"
                animate={{
                  background: [
                    "linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(59, 130, 246, 0.15) 100%)",
                    "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(236, 72, 153, 0.15) 50%, rgba(139, 92, 246, 0.15) 100%)",
                    "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 50%, rgba(236, 72, 153, 0.15) 100%)"
                  ]
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              {/* Floating particles */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: i === 0 ? 'rgba(236, 72, 153, 0.6)' : 
                               i === 1 ? 'rgba(139, 92, 246, 0.6)' : 
                               'rgba(59, 130, 246, 0.6)',
                    left: `${20 + i * 30}%`,
                    top: `${10 + i * 20}%`
                  }}
                  animate={{
                    y: [-10, 10, -10],
                    x: [-5, 5, -5],
                    opacity: [0.4, 0.8, 0.4],
                    scale: [0.8, 1.2, 0.8]
                  }}
                  transition={{
                    duration: 3 + i,
                    repeat: Infinity,
                    delay: i * 0.5,
                    ease: "easeInOut"
                  }}
                />
              ))}

              <div className="relative z-10">
                <h4 className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 mb-4 flex items-center text-lg">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      y: [0, -2, 0]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity, 
                      ease: "easeInOut"
                    }}
                  >
                    <BarChart3 className="w-6 h-6 mr-3 text-pink-500" />
                  </motion.div>
                  {post.statistics.title}
                </h4>
                <div className="grid grid-cols-2 gap-6">
                  {post.statistics.metrics.map((metric, index) => (
                    <motion.div
                      key={metric.label}
                      className="relative text-center p-4 rounded-2xl"
                      style={{
                        background: index % 2 === 0 
                          ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                        border: index % 2 === 0
                          ? '1px solid rgba(236, 72, 153, 0.2)'
                          : '1px solid rgba(59, 130, 246, 0.2)'
                      }}
                      initial={{ opacity: 0, y: 20, scale: 0.8 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: index % 2 === 0
                          ? "0 10px 20px rgba(236, 72, 153, 0.3)"
                          : "0 10px 20px rgba(59, 130, 246, 0.3)"
                      }}
                      transition={{ delay: index * 0.15 }}
                    >
                      {/* Metric glow effect */}
                      <motion.div
                        className="absolute inset-0 rounded-2xl opacity-20"
                        animate={{
                          boxShadow: index % 2 === 0
                            ? [
                                "inset 0 0 20px rgba(236, 72, 153, 0.3)",
                                "inset 0 0 30px rgba(139, 92, 246, 0.4)",
                                "inset 0 0 20px rgba(236, 72, 153, 0.3)"
                              ]
                            : [
                                "inset 0 0 20px rgba(59, 130, 246, 0.3)",
                                "inset 0 0 30px rgba(236, 72, 153, 0.4)",
                                "inset 0 0 20px rgba(59, 130, 246, 0.3)"
                              ]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: index * 0.5
                        }}
                      />

                      <div className="relative z-10 flex items-center justify-center space-x-2 mb-2">
                        <motion.span 
                          className="text-3xl font-bold text-transparent bg-clip-text"
                          style={{
                            backgroundImage: index % 2 === 0
                              ? 'linear-gradient(135deg, #ec4899, #8b5cf6)'
                              : 'linear-gradient(135deg, #3b82f6, #ec4899)'
                          }}
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ 
                            duration: 2, 
                            repeat: Infinity,
                            delay: index * 0.3
                          }}
                        >
                          {metric.value}
                        </motion.span>
                        <motion.span 
                          className="text-sm font-bold px-2 py-1 rounded-lg shadow-lg"
                          style={{
                            background: index % 2 === 0
                              ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                              : 'linear-gradient(135deg, #10b981, #059669)',
                            color: '#ffffff'
                          }}
                          animate={{ 
                            scale: [1, 1.05, 1],
                            boxShadow: [
                              "0 4px 15px rgba(251, 191, 36, 0.4)",
                              "0 6px 20px rgba(16, 185, 129, 0.5)",
                              "0 4px 15px rgba(251, 191, 36, 0.4)"
                            ]
                          }}
                          transition={{ 
                            duration: 3, 
                            repeat: Infinity,
                            delay: index * 0.2
                          }}
                        >
                          {metric.unit}
                        </motion.span>
                        {metric.trend && (
                          <motion.span
                            className={`text-xs px-3 py-1 rounded-full font-bold shadow-lg ${
                              metric.trend.includes('+') 
                                ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white' :
                              metric.trend.includes('-') 
                                ? 'bg-gradient-to-r from-red-400 to-pink-500 text-white' :
                                'bg-gradient-to-r from-amber-400 to-orange-500 text-white'
                            }`}
                            animate={{ 
                              scale: [1, 1.15, 1],
                              boxShadow: [
                                "0 4px 15px rgba(34, 197, 94, 0.3)",
                                "0 6px 20px rgba(34, 197, 94, 0.5)",
                                "0 4px 15px rgba(34, 197, 94, 0.3)"
                              ]
                            }}
                            transition={{ 
                              duration: 2.5, 
                              repeat: Infinity,
                              delay: index * 0.4
                            }}
                            whileHover={{ scale: 1.2 }}
                          >
                            {metric.trend}
                          </motion.span>
                        )}
                      </div>
                      <motion.span 
                        className="text-sm font-medium text-transparent bg-clip-text"
                        style={{
                          backgroundImage: index % 2 === 0
                            ? 'linear-gradient(135deg, #8b5cf6, #3b82f6)'
                            : 'linear-gradient(135deg, #ec4899, #8b5cf6)'
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.2 + 0.5 }}
                      >
                        {metric.label}
                      </motion.span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Engagement bar */}
        <div className="px-6 py-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            {/* Like button */}
            <motion.button
              onClick={() => handleLike(post.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 ${
                isLiked 
                  ? 'bg-red-500/20 text-red-600 border border-red-500/30' 
                  : 'bg-white/10 text-gray-600 hover:bg-red-500/10 hover:text-red-500 border border-white/20'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={isLiked ? {
                boxShadow: [
                  "0 0 0 0 rgba(239, 68, 68, 0.7)",
                  "0 0 0 10px rgba(239, 68, 68, 0)",
                  "0 0 0 0 rgba(239, 68, 68, 0)"
                ]
              } : {}}
              transition={{
                boxShadow: {
                  duration: 1,
                  repeat: isLiked ? 3 : 0,
                  ease: "easeInOut"
                }
              }}
            >
              <motion.div
                animate={isLiked ? { scale: [1, 1.3, 1] } : {}}
                transition={{ duration: 0.3 }}
              >
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              </motion.div>
              <span className="font-medium">{post.likes}</span>
              
              {/* Floating hearts animation on like */}
              <AnimatePresence>
                {showRewardAnimation === `like-${post.id}` && (
                  <>
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute pointer-events-none"
                        initial={{ 
                          opacity: 1, 
                          scale: 0,
                          x: 0,
                          y: 0
                        }}
                        animate={{ 
                          opacity: 0, 
                          scale: 1.5,
                          x: (Math.random() - 0.5) * 100,
                          y: -50 - Math.random() * 50
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ 
                          duration: 1.2,
                          delay: i * 0.1,
                          ease: "easeOut"
                        }}
                      >
                        ❤️
                      </motion.div>
                    ))}
                  </>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Comment button */}
            <motion.button
              onClick={() => setShowComments(showingComments ? null : post.id)}
              className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/10 text-gray-600 hover:bg-blue-500/10 hover:text-blue-500 border border-white/20 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium">{postComments.length}</span>
            </motion.button>

            {/* Share button */}
            <motion.button
              onClick={() => handleShare(post.id)}
              className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/10 text-gray-600 hover:bg-green-500/10 hover:text-green-500 border border-white/20 transition-all duration-300"
              whileHover={{ scale: 1.05, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Share2 className="w-5 h-5" />
              <span className="font-medium">{post.shares}</span>
              
              {/* Ripple effect on share */}
              <AnimatePresence>
                {showRewardAnimation === `share-${post.id}` && (
                  <motion.div
                    className="absolute inset-0 bg-green-400/20 rounded-full"
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ scale: 3, opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                )}
              </AnimatePresence>
            </motion.button>

            {/* Bookmark button */}
            <motion.button
              className="p-2 rounded-full bg-white/10 text-gray-600 hover:bg-yellow-500/10 hover:text-yellow-500 border border-white/20 transition-all duration-300"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
            >
              <Bookmark className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        {/* Comments section */}
        <AnimatePresence>
          {showingComments && (
            <motion.div
              className="border-t border-white/10 bg-white/5"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="p-6 space-y-4">
                {/* Existing comments */}
                {postComments.map((comment, index) => (
                  <motion.div
                    key={comment.id}
                    className="flex space-x-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {comment.user.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="bg-white/10 rounded-2xl px-4 py-3 backdrop-blur-sm">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-semibold text-gray-800 text-sm">
                            {comment.user}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(comment.timestamp)}
                          </span>
                        </div>
                        <p className="text-gray-700">{comment.text}</p>
                      </div>
                      
                      {/* Comment reactions */}
                      {comment.reactions && comment.reactions.length > 0 && (
                        <div className="flex items-center space-x-2 mt-2 ml-4">
                          {comment.reactions.map((reaction, i) => (
                            <motion.button
                              key={i}
                              className="flex items-center space-x-1 text-xs bg-white/20 rounded-full px-2 py-1 hover:bg-white/30 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <span>{reaction.emoji}</span>
                              <span className="font-medium">{reaction.count}</span>
                            </motion.button>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Add comment */}
                <motion.div
                  className="flex space-x-3 pt-4 border-t border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <img
                    src={getProfilePhotoUrl(user?.profilePhoto)}
                    alt="Your avatar"
                    className="w-8 h-8 rounded-full object-cover border border-white/30"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face';
                    }}
                  />
                  <div className="flex-1 flex space-x-2">
                    <input
                      type="text"
                      placeholder={translate('identityCircle.feed.commentPlaceholder')}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent backdrop-blur-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddComment(post.id);
                        }
                      }}
                    />
                    <motion.button
                      onClick={() => handleAddComment(post.id)}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full hover:from-blue-600 hover:to-purple-700 transition-all duration-300 font-medium shadow-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      disabled={!comment.trim()}
                    >
                      <Send className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reward animation overlay */}
        <AnimatePresence>
          {showRewardAnimation === `comment-${post.id}` && (
            <motion.div
              className="absolute inset-0 pointer-events-none flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="text-4xl"
                initial={{ scale: 0, rotate: 0 }}
                animate={{ scale: [0, 1.2, 1], rotate: [0, 360, 0] }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                💬
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.article>
    );
  }, [likedPosts, showComments, comment, user, currentTheme, translate, showRewardAnimation, handleLike, handleAddComment, handleShare]);

  // Floating Action Button for scroll to top
  const FloatingScrollButton = () => (
    <AnimatePresence>
      {showScrollTop && (
        <motion.button
          onClick={scrollToTop}
          className="fixed bottom-20 right-6 z-40 w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-2xl flex items-center justify-center"
          initial={{ opacity: 0, scale: 0, y: 100 }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: 0,
            boxShadow: [
              "0 0 20px rgba(59, 130, 246, 0.3)",
              "0 0 30px rgba(147, 51, 234, 0.4)",
              "0 0 20px rgba(59, 130, 246, 0.3)"
            ]
          }}
          exit={{ opacity: 0, scale: 0, y: 100 }}
          whileHover={{ scale: 1.1, rotate: -90 }}
          whileTap={{ scale: 0.9 }}
          transition={{
            boxShadow: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }
          }}
        >
          <ArrowUp className="w-6 h-6" />
        </motion.button>
      )}
    </AnimatePresence>
  );

  // Share Modal Component
  const ShareModal = ({ postId, onClose, onConfirm }) => (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        
        <motion.div 
          className={`relative ${currentTheme?.cardBg || 'bg-white'} rounded-2xl p-6 max-w-sm w-full shadow-2xl`}
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-8 h-8 text-white" />
            </div>
            <h3 className={`text-xl font-bold ${currentTheme?.textPrimary || 'text-gray-900'} mb-2`}>
              {translate('identityCircle.feed.sharePostTitle')}
            </h3>
            <p className={`${currentTheme?.textSecondary || 'text-gray-600'}`}>
              {translate('identityCircle.feed.sharePostDescription')}
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className={`flex-1 py-3 px-4 rounded-xl ${currentTheme?.buttonSecondary || 'bg-gray-100 hover:bg-gray-200'} ${currentTheme?.textSecondary || 'text-gray-600'} font-medium transition-colors`}
            >
              {translate('identityCircle.feed.cancel')}
            </button>
            <motion.button
              onClick={() => onConfirm(postId)}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {translate('identityCircle.feed.share')}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <div className={`min-h-screen ${currentTheme?.bg || 'bg-gray-50'}`}>
      {/* Create Button - Top of feed */}
      <div className="max-w-2xl mx-auto p-4 pb-2">
        <motion.button
          onClick={() => {
            setShowCreateModal(true);
            triggerHaptic('medium');
          }}
          className={`w-full ${currentTheme?.cardBg || 'bg-white'} rounded-2xl p-4 ${currentTheme?.border || 'border-gray-200'} border text-left transition-all duration-300 flex items-center space-x-3 relative overflow-hidden group hover:shadow-xl hover:scale-[1.02]`}
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.3), 0 0 60px rgba(59, 130, 246, 0.1)"
          }}
          whileTap={{ scale: 0.98 }}
          animate={{
            boxShadow: [
              "0 0 20px rgba(59, 130, 246, 0.1)",
              "0 0 25px rgba(59, 130, 246, 0.2)",
              "0 0 20px rgba(59, 130, 246, 0.1)"
            ]
          }}
          transition={{
            boxShadow: {
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }
          }}
        >
          {/* Animated background glow */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 group-hover:opacity-100"
            animate={{
              background: [
                "linear-gradient(45deg, rgba(59, 130, 246, 0.05), rgba(147, 51, 234, 0.05), rgba(236, 72, 153, 0.05))",
                "linear-gradient(45deg, rgba(147, 51, 234, 0.05), rgba(236, 72, 153, 0.05), rgba(59, 130, 246, 0.05))",
                "linear-gradient(45deg, rgba(236, 72, 153, 0.05), rgba(59, 130, 246, 0.05), rgba(147, 51, 234, 0.05))"
              ]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Border glow effect */}
          <motion.div
            className="absolute inset-0 rounded-2xl border-2 border-transparent opacity-0 group-hover:opacity-100"
            style={{
              background: "linear-gradient(45deg, rgba(59, 130, 246, 0.3), rgba(147, 51, 234, 0.3), rgba(236, 72, 153, 0.3)) border-box",
              WebkitMask: "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude"
            }}
            animate={{
              background: [
                "linear-gradient(45deg, rgba(59, 130, 246, 0.3), rgba(147, 51, 234, 0.3), rgba(236, 72, 153, 0.3)) border-box",
                "linear-gradient(90deg, rgba(147, 51, 234, 0.3), rgba(236, 72, 153, 0.3), rgba(59, 130, 246, 0.3)) border-box",
                "linear-gradient(135deg, rgba(236, 72, 153, 0.3), rgba(59, 130, 246, 0.3), rgba(147, 51, 234, 0.3)) border-box"
              ]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />

          <motion.img
            src={getProfilePhotoUrl(user?.profilePhoto)}
            alt="Your avatar"
            className={`w-10 h-10 rounded-full object-cover border-2 ${currentTheme?.border || 'border-gray-200'} relative z-10`}
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face';
            }}
            whileHover={{ scale: 1.1, rotate: 5 }}
          />
          <span className={`flex-1 ${currentTheme?.textMuted || 'text-gray-500'} relative z-10 group-hover:text-blue-600 transition-colors`}>
            {translate('identityCircle.feed.createPlaceholder')}
          </span>
          <motion.div
            className="relative z-10"
            whileHover={{ rotate: 90, scale: 1.2 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Plus className={`w-5 h-5 ${currentTheme?.textSecondary || 'text-gray-500'} group-hover:text-blue-600 transition-colors`} />
          </motion.div>
          
          {/* Sparkle effects */}
          <motion.div
            className="absolute top-2 right-8 w-1 h-1 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100"
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: 0.5
            }}
          />
          <motion.div
            className="absolute bottom-3 left-20 w-1 h-1 bg-purple-400 rounded-full opacity-0 group-hover:opacity-100"
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: 1
            }}
          />
        </motion.button>
      </div>

      {/* Feed Posts */}
      <div className="max-w-2xl mx-auto px-4 pb-4">
        {allFeedData.map(renderPost)}
      </div>

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          user={showProfileModal}
          onClose={closeProfileModal}
        />
      )}

      {/* Create Content Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCreateModal(false)}
          >
            {/* Backdrop with blur */}
            <motion.div 
              className="absolute inset-0 bg-black/60 backdrop-blur-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Modal Content */}
            <motion.div 
              className={`relative ${currentTheme?.cardBg || 'bg-white'} rounded-3xl p-8 max-w-lg w-full shadow-2xl border ${currentTheme?.border || 'border-gray-200'} overflow-hidden`}
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Animated background gradients */}
              <motion.div
                className="absolute inset-0 opacity-20"
                animate={{
                  background: [
                    "radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)",
                    "radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)",
                    "radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)",
                    "radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)"
                  ]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              {/* Header */}
              <div className="relative z-10 text-center mb-8">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                  animate={{ 
                    scale: [1, 1.05, 1],
                    boxShadow: [
                      "0 0 20px rgba(139, 92, 246, 0.3)",
                      "0 0 30px rgba(236, 72, 153, 0.4)",
                      "0 0 20px rgba(139, 92, 246, 0.3)"
                    ]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut"
                  }}
                >
                  <Sparkles className="w-8 h-8 text-white" />
                </motion.div>
                <h2 className={`text-3xl font-bold ${currentTheme?.textPrimary || 'text-gray-900'} mb-2`}>
                  {translate('identityCircle.feed.createTitle')}
                </h2>
                <p className={`${currentTheme?.textSecondary || 'text-gray-600'}`}>
                  {translate('identityCircle.feed.createDescription')}
                </p>
              </div>
              
              {/* Content Type Grid */}
              <div className="relative z-10 grid grid-cols-2 gap-4 mb-6">
                {createOptions().filter(opt => opt.id !== 'vault').map((option, index) => {
                  const IconComponent = option.icon;
                  return (
                    <motion.button
                      key={option.id}
                      className={`p-6 rounded-2xl bg-gradient-to-br ${option.gradient} text-white flex flex-col items-center space-y-3 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-white/20`}
                      whileHover={{ 
                        scale: 1.05, 
                        y: -5,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleCreateContent(option.id)}
                    >
                      <motion.div
                        className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"
                        whileHover={{ 
                          scale: 1.1,
                          y: -2
                        }}
                      >
                        <IconComponent className="w-6 h-6" />
                      </motion.div>
                      <span className="text-sm font-semibold text-center leading-tight">
                        {option.title}
                      </span>
                      
                      {/* Floating particles for each option */}
                      <motion.div
                        className="absolute inset-0 pointer-events-none"
                        animate={{
                          opacity: [0, 0.5, 0]
                        }}
                        transition={{
                          duration: 3 + index,
                          repeat: Infinity,
                          delay: index * 0.5
                        }}
                      >
                        {[...Array(3)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-1 h-1 bg-white rounded-full"
                            style={{
                              left: `${20 + i * 30}%`,
                              top: `${20 + i * 20}%`
                            }}
                            animate={{
                              y: [-10, -30, -10],
                              opacity: [0, 1, 0],
                              scale: [0, 1, 0]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              delay: i * 0.3 + index * 0.1
                            }}
                          />
                        ))}
                      </motion.div>
                    </motion.button>
                  );
                })}
              </div>
              
              {/* Vault option */}
              <motion.button
                className="relative z-10 w-full p-4 bg-gradient-to-r from-slate-600 via-zinc-700 to-gray-800 text-white rounded-2xl mb-6 shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center space-x-3 border-2 border-white/10"
                whileHover={{ 
                  scale: 1.02, 
                  y: -2,
                  boxShadow: "0 15px 30px rgba(0,0,0,0.3)"
                }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => handleCreateContent('vault')}
              >
                <motion.div
                  className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"
                  whileHover={{ rotate: -10, scale: 1.1 }}
                >
                  <Lock className="w-5 h-5" />
                </motion.div>
                <span className="font-semibold">{translate('identityCircle.feed.digitalVault')}</span>
                
                {/* Lock particles */}
                <motion.div
                  className="absolute right-4"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  🔒
                </motion.div>
              </motion.button>
              
              {/* Close button */}
              <motion.button 
                onClick={() => setShowCreateModal(false)}
                className={`relative z-10 w-full p-3 ${currentTheme?.buttonSecondary || 'bg-gray-100 hover:bg-gray-200'} ${currentTheme?.textSecondary || 'text-gray-600'} rounded-xl transition-all duration-300 font-medium`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {translate('identityCircle.feed.close')}
              </motion.button>
              
              {/* Floating elements */}
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-30"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`
                  }}
                  animate={{
                    y: [-20, 20, -20],
                    x: [-10, 10, -10],
                    opacity: [0.3, 0.8, 0.3],
                    scale: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 3 + i,
                    repeat: Infinity,
                    delay: i * 0.5,
                    ease: "easeInOut"
                  }}
                />
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Privacy Modal */}
      <AnimatePresence>
        {showPrivacyModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPrivacyModal(false)}
          >
            {/* Backdrop with blur */}
            <motion.div 
              className="absolute inset-0 bg-black/70 backdrop-blur-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Modal Content */}
            <motion.div 
              className={`relative ${currentTheme?.cardBg || 'bg-white'} rounded-3xl p-8 max-w-lg w-full shadow-2xl border ${currentTheme?.border || 'border-gray-200'} overflow-hidden`}
              initial={{ scale: 0.8, opacity: 0, y: 50, rotateY: -90 }}
              animate={{ scale: 1, opacity: 1, y: 0, rotateY: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50, rotateY: 90 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Animated background gradients */}
              <motion.div
                className="absolute inset-0 opacity-10"
                animate={{
                  background: [
                    "radial-gradient(circle at 30% 30%, rgba(16, 185, 129, 0.4) 0%, transparent 60%)",
                    "radial-gradient(circle at 70% 30%, rgba(59, 130, 246, 0.4) 0%, transparent 60%)",
                    "radial-gradient(circle at 70% 70%, rgba(147, 51, 234, 0.4) 0%, transparent 60%)",
                    "radial-gradient(circle at 30% 70%, rgba(245, 101, 101, 0.4) 0%, transparent 60%)"
                  ]
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              {/* Header */}
              <div className="relative z-10 text-center mb-8">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                  animate={{ 
                    scale: [1, 1.05, 1],
                    boxShadow: [
                      "0 0 20px rgba(16, 185, 129, 0.3)",
                      "0 0 30px rgba(59, 130, 246, 0.3)",
                      "0 0 20px rgba(147, 51, 234, 0.3)",
                      "0 0 30px rgba(16, 185, 129, 0.3)"
                    ]
                  }}
                  transition={{ 
                    scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                    boxShadow: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                  }}
                >
                  <Shield className="w-8 h-8 text-white" />
                </motion.div>
                <h2 className={`text-3xl font-bold ${currentTheme?.textPrimary || 'text-gray-900'} mb-2`}>
                  {translate('identityCircle.feed.privacyTitle')}
                </h2>
                <p className={`${currentTheme?.textSecondary || 'text-gray-600'}`}>
                  {translate('identityCircle.feed.privacyDescription')}
                </p>
              </div>
              
              {/* Privacy Options */}
              <div className="relative z-10 space-y-4 mb-6">
                {privacyOptions().map((option, index) => {
                  const IconComponent = option.icon;
                  const isSelected = selectedPrivacy === option.id;
                  return (
                    <motion.button
                      key={option.id}
                                           className={`w-full p-5 rounded-2xl bg-gradient-to-r ${option.gradient} flex items-center space-x-4 text-white shadow-xl border-2 transition-all ${
                        isSelected ? 'border-white/40 scale-105' : 'border-white/10'
                      }`}
                      whileHover={{ scale: isSelected ? 1.05 : 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handlePrivacySelect(option.id)}
                    >
                      <motion.div 
                        className="w-12 h-12 bg-white/25 rounded-xl flex items-center justify-center"
                        whileHover={{ rotate: 10, scale: 1.1 }}
                      >
                        <IconComponent className="w-6 h-6" />
                      </motion.div>
                      <div className="flex-1 text-left">
                        <h3 className="font-bold text-lg">{option.title}</h3>
                        <p className="text-sm text-white/90 mt-1">{option.description}</p>
                      </div>
                      <motion.div
                        className={`w-6 h-6 border-2 border-white/50 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-white/20' : ''
                        }`}
                        whileHover={{ scale: 1.2 }}
                        animate={isSelected ? { 
                          boxShadow: [
                            "0 0 10px rgba(255, 255, 255, 0.5)",
                            "0 0 20px rgba(255, 255, 255, 0.8)",
                            "0 0 10px rgba(255, 255, 255, 0.5)"
                          ]
                        } : {}}
                        transition={{
                          boxShadow: {
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }
                        }}
                      >
                        {isSelected && (
                          <motion.div
                            className="w-3 h-3 bg-white rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          />
                        )}
                      </motion.div>
                      
                      {/* Option particles */}
                      {isSelected && (
                        <motion.div
                          className="absolute inset-0 pointer-events-none"
                          animate={{ opacity: [0, 0.3, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          {[...Array(4)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="absolute w-1 h-1 bg-white rounded-full"
                              style={{
                                left: `${15 + i * 20}%`,
                                top: `${30 + (i % 2) * 40}%`
                              }}
                              animate={{
                                y: [-5, -15, -5],
                                opacity: [0, 1, 0],
                                scale: [0, 1, 0]
                              }}
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                delay: i * 0.2
                              }}
                            />
                          ))}
                        </motion.div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
              
              {/* Close button */}
              <motion.button 
                onClick={() => setShowPrivacyModal(false)}
                className={`relative z-10 w-full p-3 ${currentTheme?.buttonSecondary || 'bg-gray-100 hover:bg-gray-200'} ${currentTheme?.textSecondary || 'text-gray-600'} rounded-xl transition-all duration-300 font-medium`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                {translate('identityCircle.feed.close')}
              </motion.button>
              
              {/* Floating security icons */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute pointer-events-none opacity-20"
                  style={{
                    left: `${10 + Math.random() * 80}%`,
                    top: `${10 + Math.random() * 80}%`
                  }}
                  animate={{
                    y: [-15, 15, -15],
                    x: [-10, 10, -10],
                    rotate: [0, 360, 0],
                    opacity: [0.1, 0.3, 0.1]
                  }}
                  transition={{
                    duration: 4 + i,
                    repeat: Infinity,
                    delay: i * 0.7,
                    ease: "easeInOut"
                  }}
                >
                  {i % 3 === 0 ? (
                    <Shield className="w-4 h-4 text-blue-500" />
                  ) : i % 3 === 1 ? (
                    <Lock className="w-4 h-4 text-green-500" />
                  ) : (
                    <Users className="w-4 h-4 text-purple-500" />
                  )}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IdentityFeed;
